import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import SignatureWorkflowModal from './SignatureWorkflowModal';
import { useLanguage } from '../../../contexts/LanguageContext';

const SignatureWorkflowPanel = ({ currentLanguage }) => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleNewWorkflow = () => {
    setIsModalOpen(true);
  };

  const workflows = [
  {
    id: 'wf-001',
    documentName: 'Contrat Omra - Groupe A',
    initiator: {
      name: 'Mohammed Tazi',
      role: 'Administrateur',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3978aa8-1763292887874.png",
      avatarAlt: 'Professional headshot of Moroccan administrator with glasses wearing dark suit'
    },
    signers: [
    {
      name: 'Ahmed Benali',
      status: 'signed',
      signedAt: new Date('2026-01-28T10:30:00'),
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_128b56b6a-1763292153045.png",
      avatarAlt: 'Professional headshot of Moroccan man with short black hair wearing traditional white djellaba'
    },
    {
      name: 'Fatima Zahra',
      status: 'pending',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eec778e9-1766069487817.png",
      avatarAlt: 'Professional headshot of Moroccan woman with hijab in elegant beige color'
    },
    {
      name: 'Youssef Alami',
      status: 'pending',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15698c709-1763292831085.png",
      avatarAlt: 'Professional headshot of Moroccan man with beard wearing navy blue suit'
    }],

    progress: 33,
    createdAt: new Date('2026-01-25'),
    deadline: new Date('2026-02-05')
  },
  {
    id: 'wf-002',
    documentName: 'Attestation Hajj - Groupe Principal',
    initiator: {
      name: 'Laila Bennani',
      role: 'Agent',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1803d733f-1763296538713.png",
      avatarAlt: 'Professional headshot of Moroccan travel agent with warm smile wearing burgundy blazer'
    },
    signers: [
    {
      name: 'Hassan Idrissi',
      status: 'signed',
      signedAt: new Date('2026-01-29T14:15:00'),
      avatar: "https://images.unsplash.com/photo-1728296156183-e1c4735b5cb3",
      avatarAlt: 'Professional headshot of elderly Moroccan man with white beard wearing traditional kaftan'
    },
    {
      name: 'Khadija Mansouri',
      status: 'signed',
      signedAt: new Date('2026-01-30T09:45:00'),
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19d90f11a-1763294201313.png",
      avatarAlt: 'Professional headshot of Moroccan woman with elegant hijab in navy blue'
    }],

    progress: 100,
    createdAt: new Date('2026-01-28'),
    deadline: new Date('2026-02-10')
  }];


  const statusConfig = {
    signed: {
      label: t('documents.signed'),
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle'
    },
    pending: {
      label: t('documents.pending'),
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      icon: 'Clock'
    }
  };

  const formatDateTime = (date) => {
    // Always use en-US locale for universal date/time format (Western numerals)
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {t('documents.signatureWorkflows')}
          </h2>
          <button
            onClick={handleNewWorkflow}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth text-sm">
            <Icon name="Plus" size={16} />
            {t('documents.newWorkflow')}
          </button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {workflows?.map((workflow) =>
        <div key={workflow?.id} className="p-4 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">
                  {workflow?.documentName}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden">
                    <Image
                    src={workflow?.initiator?.avatar}
                    alt={workflow?.initiator?.avatarAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div>
                    <p className="text-sm text-foreground">{workflow?.initiator?.name}</p>
                    <p className="caption text-muted-foreground">{workflow?.initiator?.role}</p>
                  </div>
                </div>
              </div>

              <div className={`
                px-3 py-1 rounded-full text-sm font-medium
                ${workflow?.progress === 100 ?
            'bg-success/10 text-success' : 'bg-warning/10 text-warning'}
              `
            }>
                {workflow?.progress}%
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="caption text-muted-foreground">
                  {t('documents.progress')}
                </span>
                <span className="caption text-muted-foreground">
                  {workflow?.signers?.filter((s) => s?.status === 'signed')?.length} / {workflow?.signers?.length}
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                className={`h-full transition-all duration-500 ${
                workflow?.progress === 100 ? 'bg-success' : 'bg-warning'}`
                }
                style={{ width: `${workflow?.progress}%` }} />

              </div>
            </div>

            <div className="space-y-3 mb-4">
              {workflow?.signers?.map((signer, index) => {
              const status = statusConfig?.[signer?.status];
              return (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                      src={signer?.avatar}
                      alt={signer?.avatarAlt}
                      className="w-full h-full object-cover" />

                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {signer?.name}
                      </p>
                      {signer?.signedAt &&
                    <p className="caption text-muted-foreground">
                          {formatDateTime(signer?.signedAt)}
                        </p>
                    }
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status?.bgColor} flex-shrink-0`}>
                      <Icon name={status?.icon} size={14} className={status?.color} />
                      <span className={`caption font-medium ${status?.color}`}>
                        {status?.label}
                      </span>
                    </div>
                  </div>);

            })}
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                {t('documents.createdOn')} {formatDateTime(workflow?.createdAt)}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                {t('documents.deadline')} {formatDateTime(workflow?.deadline)}
              </span>
            </div>
          </div>
        )}
      </div>

      <SignatureWorkflowModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentLanguage={currentLanguage} />
      
    </div>);

};

export default SignatureWorkflowPanel;